import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { collect } from 'collect.js'
import axios from 'axios'
import { useOrderStore } from '@/stores'

axios.defaults.headers.common['X-Project'] = import.meta.env.VITE_PROJECT_ALIAS

export const useTrustyStore = defineStore('trustyComplete', () => {
  const orderStore = useOrderStore()
  const data = ref([])
  const inputValue = ref('')
  const pickupRef = ref('')
  const dropoffRef = ref('')
  const pathStartFinish = ref({
    pickup: {
      lat: null,
      lng: null
    },
    dropoff: {
      lat: null,
      lng: null
    },
    valid: {
      pickup: false,
      dropoff: false
    }
  })

  const mainTypes = ref({
    pickup: null,
    dropoff: null
  })

  const activeAuto = ref('')

  const showSuggestions = ref({
    pickup: false,
    dropoff: false
  })

  const lastChoisePlace = ref({
    pickup: {
      formatted_address: '',
      place_id: ''
    },
    dropoff: {
      formatted_address: '',
      place_id: ''
    }
  })

  const placeChoised = ref({
    pickup: false,
    dropoff: false
  })

  const apiInput = import.meta.env.VITE_APP_API_URL + '/autocomplete'

  const updatePlaceChoised = (val, type) => {
    if (type == 'pickup') {
      placeChoised.value.pickup = val
    } else {
      placeChoised.value.dropoff = val
    }
  }

  const clearInput = (mode) => {
    if (mode === 'dropoff') {
      dropoffRef.value = ''
      orderStore.update({
        dropoff: ''
      })
    }
    if (mode === 'pickup') {
      pickupRef.value = ''
      orderStore.update({
        pickup: ''
      })
    }
  }

  const resetPathStartFinish = () => {
    pathStartFinish.value = {
      pickup: {
        lat: null,
        lng: null
      },
      dropoff: {
        lat: null,
        lng: null
      },
      valid: {
        pickup: false,
        dropoff: false
      }
    }
  }

  const updateLastChoisePlace = (val, type) => {
    if (type == 'pickup') {
      lastChoisePlace.value.pickup = val
    } else {
      lastChoisePlace.value.dropoff = val
    }
  }

  const updateSuggestions = (val) => {
    data.value = val
  }

  const updatePathStartFinish = (val, type) => {
    if (type == 'pickup') {
      pathStartFinish.value.pickup = val
      pathStartFinish.value.valid.pickup = true
    } else {
      pathStartFinish.value.dropoff = val
      pathStartFinish.value.valid.dropoff = true
    }
  }

  const isSelectSuggestion = ref(false)

  const updateMainTypes = (typeAddress, type = 'pickup') => {
    if (type == 'pickup') mainTypes.value = { ...mainTypes.value, pickup: typeAddress }
    if (type == 'dropoff') mainTypes.value = { ...mainTypes.value, dropoff: typeAddress }
  }

  // Определение типа точки по ключевым словам
  function guessPlaceType(place) {
    const name = (place.name || '').toLowerCase()
    const address = (place.formatted_address || '').toLowerCase()
    const types = Array.isArray(place.types) ? place.types : []
    const priorityTypes = ['airport', 'port', 'transit_station', 'train_station']
    // Сначала ищем нужный тип в types
    for (const t of priorityTypes) {
      if (types.includes(t)) return t
    }
    // Если не найден — ищем по ключевым словам
    const keywords = [
      { type: 'airport', words: ['airport', 'aeroporto', 'aéroport', 'flughafen', 'аэропорт'] },
      { type: 'port', words: ['port', 'harbor', 'porto', 'port de', 'molo', 'pier', 'порт'] },
      {
        type: 'transit_station',
        words: [
          'transit',
          'terminal',
          'hub',
          'interchange',
          'commuter',
          'автовокзал',
          'bus terminal',
          'автостанция',
          'пересадочный узел',
          'station centrale'
        ]
      },
      {
        type: 'train_station',
        words: [
          'train station',
          'railway station',
          'bahnhof',
          'gare',
          'estación',
          'stazione',
          'вокзал'
        ]
      }
    ]
    for (const { type, words } of keywords) {
      for (const w of words) {
        if (name.includes(w) || address.includes(w)) return type
      }
    }
    return 'other'
  }

  const selectSuggestions = async (type, place_id) => {
    isSelectSuggestion.value = true
    const response = await axios.get(`${apiInput}/details?place_id=${place_id}`)
    let latLng = await response.data.result.geometry.location

    const placeType = guessPlaceType(response.data.result)
    await updateMainTypes(placeType, type)

    if (!checkAddress(response.data.result, type)) return
    updatePathStartFinish(latLng, type)
    updateLastChoisePlace({
      place_id: response.data.result.place_id,
      formatted_address: response.data.result.formatted_address
    })
  }

  watch(isSelectSuggestion, (x) => {
    if (x) {
      setTimeout(() => {
        isSelectSuggestion.value = false
      }, 700)
    }
  })

  // Проверка, находится ли страна в числе элементов
  const checkAddress = (address, type) => {
    const countries =
      'Austria, Belgium, Bulgaria, Greece, Denmark, Ireland, Spain, Italy, Cyprus, Latvia, Lithuania, Luxembourg, Malta, Netherlands, Poland, Portugal, Romania, Slovakia, Slovenia, Finland, France, Croatia, Czechia, Sweden, Estonia, Germany, Hungary, Switzerland, Vatican City, San Marino, Serbia, Bosnia and Herzegovina,  Montenegro, North Macedonia, Albania'
    let addressCollect = collect(address.address_components)
    let findedCountry = addressCollect
      .filter((e) => {
        return e.types.includes('country')
      })
      .first()

    if (findedCountry) {
      let countryChecked = findedCountry.long_name

      if (countries.includes(countryChecked)) {
        type == 'pickup'
          ? (pathStartFinish.value.valid.pickup = true)
          : (pathStartFinish.value.valid.dropoff = true)

        return true
      }

      type == 'pickup'
        ? (pathStartFinish.value.valid.pickup = false)
        : (pathStartFinish.value.valid.dropoff = false)
    }
    return false
  }

  let timer = false
  const fetchSuggestions = async (query, fieldType = false) => {
    clearTimeout(timer)
    timer = setTimeout(async () => {
      if (query.length === 0) return
      const queryParam = String(query).toLowerCase()
      try {
        const response = await axios.get(`${apiInput}/input?input=${queryParam}`)
        if (!isSelectSuggestion.value) {
          data.value = await response.data.predictions
          if (fieldType) showSuggestions.value[fieldType] = true
        }
      } catch (error) {
        console.error('Error fetching suggestions')
      }
    }, 250)
  }

  const findPlace = async (query, callback) => {
    try {
      const response = await axios.get(`${apiInput}/findPlace?input=${query}`)
      callback(response.data)
    } catch (error) {
      console.error('Error fetching suggestions')
    }
  }

  const $reset = () => {
    clearInput('dropoff')
    clearInput('pickup')
    updateLastChoisePlace('', 'pickup')
    updateLastChoisePlace('', 'dropoff')
    placeChoised.value = {
      pickup: false,
      dropoff: false
    }
    pickupRef.value = ''
    dropoffRef.value = ''
  }

  return {
    inputValue,
    pickupRef,
    dropoffRef,
    data,
    pathStartFinish,
    activeAuto,
    showSuggestions,
    lastChoisePlace,
    placeChoised,
    mainTypes,
    $reset,
    resetPathStartFinish,
    updatePathStartFinish,
    findPlace,
    updatePlaceChoised,
    updateLastChoisePlace,
    checkAddress,
    selectSuggestions,
    updateSuggestions,
    fetchSuggestions,
    updateMainTypes,
    clearInput
  }
})
